import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export const getIssuesOverview = createAsyncThunk(
    'issues/getIssuesOverview',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getIssuesOverview();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Issues Overview');
        }
    }
);

export const fetchIssues = createAsyncThunk(
    'issues/fetchIssues',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getIssuesPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch issues');
        }
    }
);

export const fetchUserIssues = createAsyncThunk(
    'issues/fetchUserIssues',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getUserIssuesPaginated(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch issues');
        }
    }
);

export const deleteIssue = createAsyncThunk(
    'issues/deleteIssue',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiService.deleteIssue(id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete issue');
        }
    }
);

export const issuePdfExport = createAsyncThunk(
    'issues/issuePdfExport',
    async (getIds, { rejectWithValue }) => {
        try {
            const response = await apiService.issuePdfExport(getIds);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to export');
        }
    }
);

export const issueExcelExport = createAsyncThunk(
    'issues/issueExcelExport',
    async (getIds, { rejectWithValue }) => {
        try {
            const response = await apiService.issueExcelExport(getIds);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to export');
        }
    }
);

export const issueLastId = createAsyncThunk(
    'issues/issueLastId',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.issueLastId();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to get IDs');
        }
    }
);

export const getIssueById = createAsyncThunk(
    'issues/getIssueById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiService.getIssueById(id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to get data');
        }
    }
);

export const getServiceEntryDropdown = createAsyncThunk(
    'issues/getServiceEntryDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getServiceEntryDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to get Service Entry');
        }
    }
);

export const createLinkIssueApi = createAsyncThunk(
    'issues/createLinkIssueApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.createLinkIssue(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create link issue');
        }
    }
);

export const updatePriorityApi = createAsyncThunk(
    'issues/updatePriorityApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updatePriority(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update priority');
        }
    }
);

export const updateStatusApi = createAsyncThunk(
    'issues/updateStatusApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updateStatus(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update status');
        }
    }
);

export const updateAssigneeApi = createAsyncThunk(
    'issues/updateAssigneeApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updateAssignee(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update assignee');
        }
    }
);

export const updateTagsApi = createAsyncThunk(
    'issues/updateTagsApi',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.updateTags(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update tags');
        }
    }
);

export const issueHistory = createAsyncThunk(
    'issues/issueHistory',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getIssueHistory(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch issues history');
        }
    }
);

export const commentOnIssue = createAsyncThunk(
    'issues/commentOnIssue',
    async (data, { rejectWithValue }) => {
        try {
            const response = await apiService.commentOnIssue(data);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to comment on issue');
        }
    }
);

export const getIssueTagsDropdown = createAsyncThunk(
    'issues/getIssueTagsDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getIssueTagsDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to issue tags');
        }
    }
);

const initialState = {

    issuesOverviewData: null,
    issuesOverviewLoading: false,
    issuesOverviewError: null,

    issuesData: null,
    issuesLoading: false,
    issuesError: null,

    deleteIssueLoading: false,
    deleteIssueError: null,

    issuePdfExportLoading: false,
    issuePdfExportData: {},
    issuePdfExportError: null,

    issueExcelExportLoading: false,
    issueExcelExportData: {},
    issueExcelExportError: null,

    issueLastIdData: null,
    issueLastIdLoading: false,
    issueLastIdError: null,

    issueIdData: null,
    issueIdLoading: false,
    issueIdError: null,

    serviceEntryData: null,
    serviceEntryLoading: false,
    serviceEntryError: null,

    createLinkIssueData: null,
    createLinkIssueLoading: false,
    createLinkIssueError: null,

    updatePriorityData: null,
    updatePriorityLoading: false,
    updatePriorityError: null,

    updateStatusData: null,
    updateStatusLoading: false,
    updateStatusError: null,

    issueHistoryData: null,
    issueHistoryLoading: false,
    issueHistoryError: null,

    commentOnIssueData: null,
    commentOnIssueLoading: false,
    commentOnIssueError: null,

    updateAssigneeData: null,
    updateAssigneeLoading: false,
    updateAssigneeError: null,

    updateTagsData: null,
    updateTagsLoading: false,
    updateTagsError: null,

    fetchUserIssuesData: null,
    fetchUserIssuesLoading: false,
    fetchUserIssuesError: null,

    getIssueTagsDropdownData: null,
    getIssueTagsDropdownLoading: false,
    getIssueTagsDropdownError: null

};

const issuesSlice = createSlice({
    name: 'issues',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder

            .addCase(getIssuesOverview.pending, (state) => {
                state.issuesOverviewLoading = true;
                state.issuesOverviewError = null;
            })
            .addCase(getIssuesOverview.fulfilled, (state, action) => {
                state.issuesOverviewLoading = false;
                state.issuesOverviewData = action.payload?.data || null;
                state.issuesOverviewError = null;
            })
            .addCase(getIssuesOverview.rejected, (state, action) => {
                state.issuesOverviewLoading = false;
                state.issuesOverviewError = action.payload;
                state.issuesOverviewData = null;
            })
            .addCase(fetchIssues.pending, (state) => {
                state.issuesLoading = true;
                state.issuesError = null;
            })
            .addCase(fetchIssues.fulfilled, (state, action) => {
                state.issuesLoading = false;
                state.issuesData = action.payload?.data || null;
                state.issuesError = null;
            })
            .addCase(fetchIssues.rejected, (state, action) => {
                state.issuesLoading = false;
                state.issuesError = action.payload;
                state.issuesData = null;
            })
            .addCase(deleteIssue.pending, (state) => {
                state.deleteIssueLoading = true;
                state.deleteIssueError = null;
            })
            .addCase(deleteIssue.fulfilled, (state, action) => {
                state.deleteIssueLoading = false;
                state.deleteIssueError = null;
            })
            .addCase(deleteIssue.rejected, (state, action) => {
                state.deleteIssueLoading = false;
                state.deleteIssueError = action.payload;
            })

            .addCase(issuePdfExport.pending, (state) => {
                state.issuePdfExportLoading = true;
                state.issuePdfExportError = null;
            })
            .addCase(issuePdfExport.fulfilled, (state, action) => {
                state.issuePdfExportLoading = false;
                state.issuePdfExportData = action.payload?.data || {};
                state.issuePdfExportError = null;
            })
            .addCase(issuePdfExport.rejected, (state, action) => {
                state.issuePdfExportLoading = false;
                state.issuePdfExportError = action.payload;
            })

            // Excel Export
            .addCase(issueExcelExport.pending, (state) => {
                state.issueExcelExportLoading = true;
                state.issueExcelExportError = null;
            })
            .addCase(issueExcelExport.fulfilled, (state, action) => {
                state.issueExcelExportLoading = false;
                state.issueExcelExportData = action.payload?.data || {};
                state.issueExcelExportError = null;
            })
            .addCase(issueExcelExport.rejected, (state, action) => {
                state.issueExcelExportLoading = false;
                state.issueExcelExportError = action.payload;
            })

            // Last ID
            .addCase(issueLastId.pending, (state) => {
                state.issueLastIdLoading = true;
                state.issueLastIdError = null;
            })
            .addCase(issueLastId.fulfilled, (state, action) => {
                state.issueLastIdLoading = false;
                state.issueLastIdData = action.payload?.data || null;
                state.issueLastIdError = null;
            })
            .addCase(issueLastId.rejected, (state, action) => {
                state.issueLastIdLoading = false;
                state.issueLastIdError = action.payload;
                state.issueLastIdData = null;
            })

            // Get Issue by ID
            .addCase(getIssueById.pending, (state) => {
                state.issueIdLoading = true;
                state.issueIdError = null;
            })
            .addCase(getIssueById.fulfilled, (state, action) => {
                state.issueIdLoading = false;
                state.issueIdData = action.payload?.data || null;
                state.issueIdError = null;
            })
            .addCase(getIssueById.rejected, (state, action) => {
                state.issueIdLoading = false;
                state.issueIdError = action.payload;
                state.issueIdData = null;
            })

            // Get Service Entry Dropdown
            .addCase(getServiceEntryDropdown.pending, (state) => {
                state.serviceEntryLoading = true;
                state.serviceEntryError = null;
            })
            .addCase(getServiceEntryDropdown.fulfilled, (state, action) => {
                state.serviceEntryLoading = false;
                state.serviceEntryData = action.payload?.data || null;
                state.serviceEntryError = null;
            })
            .addCase(getServiceEntryDropdown.rejected, (state, action) => {
                state.serviceEntryLoading = false;
                state.serviceEntryError = action.payload;
                state.serviceEntryData = null;
            })

            // Create Link Issue
            .addCase(createLinkIssueApi.pending, (state) => {
                state.createLinkIssueLoading = true;
                state.createLinkIssueError = null;
            })
            .addCase(createLinkIssueApi.fulfilled, (state, action) => {
                state.createLinkIssueLoading = false;
                state.createLinkIssueData = action.payload?.data || null;
                state.createLinkIssueError = null;
            })
            .addCase(createLinkIssueApi.rejected, (state, action) => {
                state.createLinkIssueLoading = false;
                state.createLinkIssueError = action.payload;
                state.createLinkIssueData = null;
            })

            // Update Priority
            .addCase(updatePriorityApi.pending, (state) => {
                state.updatePriorityLoading = true;
                state.updatePriorityError = null;
            })
            .addCase(updatePriorityApi.fulfilled, (state, action) => {
                state.updatePriorityLoading = false;
                state.updatePriorityData = action.payload?.data || null;
                state.updatePriorityError = null;
            })
            .addCase(updatePriorityApi.rejected, (state, action) => {
                state.updatePriorityLoading = false;
                state.updatePriorityError = action.payload;
                state.updatePriorityData = null;
            })

            // Update Status
            .addCase(updateStatusApi.pending, (state) => {
                state.updateStatusLoading = true;
                state.updateStatusError = null;
            })
            .addCase(updateStatusApi.fulfilled, (state, action) => {
                state.updateStatusLoading = false;
                state.updateStatusData = action.payload?.data || null;
                state.updateStatusError = null;
            })
            .addCase(updateStatusApi.rejected, (state, action) => {
                state.updateStatusLoading = false;
                state.updateStatusError = action.payload;
                state.updateStatusData = null;
            })

            // Issue History
            .addCase(issueHistory.pending, (state) => {
                state.issueHistoryLoading = true;
                state.issueHistoryError = null;
            })
            .addCase(issueHistory.fulfilled, (state, action) => {
                state.issueHistoryLoading = false;
                state.issueHistoryData = action.payload?.data || null;
                state.issueHistoryError = null;
            })
            .addCase(issueHistory.rejected, (state, action) => {
                state.issueHistoryLoading = false;
                state.issueHistoryError = action.payload;
                state.issueHistoryData = null;
            })

            // Comment on Issue
            .addCase(commentOnIssue.pending, (state) => {
                state.commentOnIssueLoading = true;
                state.commentOnIssueError = null;
            })
            .addCase(commentOnIssue.fulfilled, (state, action) => {
                state.commentOnIssueLoading = false;
                state.commentOnIssueData = action.payload?.data || null;
                state.commentOnIssueError = null;
            })
            .addCase(commentOnIssue.rejected, (state, action) => {
                state.commentOnIssueLoading = false;
                state.commentOnIssueError = action.payload;
                state.commentOnIssueData = null;
            })

            // Update Assignee
            .addCase(updateAssigneeApi.pending, (state) => {
                state.updateAssigneeLoading = true;
                state.updateAssigneeError = null;
            })
            .addCase(updateAssigneeApi.fulfilled, (state, action) => {
                state.updateAssigneeLoading = false;
                state.updateAssigneeData = action.payload?.data || null;
                state.updateAssigneeError = null;
            })
            .addCase(updateAssigneeApi.rejected, (state, action) => {
                state.updateAssigneeLoading = false;
                state.updateAssigneeError = action.payload;
                state.updateAssigneeData = null;
            })

            // Update Tags
            .addCase(updateTagsApi.pending, (state) => {
                state.updateTagsLoading = true;
                state.updateTagsError = null;
            })
            .addCase(updateTagsApi.fulfilled, (state, action) => {
                state.updateTagsLoading = false;
                state.updateTagsData = action.payload?.data || null;
                state.updateTagsError = null;
            })
            .addCase(updateTagsApi.rejected, (state, action) => {
                state.updateTagsLoading = false;
                state.updateTagsError = action.payload;
                state.updateTagsData = null;
            })

            // Fetch User Issues
            .addCase(fetchUserIssues.pending, (state) => {
                state.fetchUserIssuesLoading = true;
                state.fetchUserIssuesError = null;
            })
            .addCase(fetchUserIssues.fulfilled, (state, action) => {
                state.fetchUserIssuesLoading = false;
                state.fetchUserIssuesData = action.payload?.data || null;
                state.fetchUserIssuesError = null;
            })
            .addCase(fetchUserIssues.rejected, (state, action) => {
                state.fetchUserIssuesLoading = false;
                state.fetchUserIssuesError = action.payload;
                state.fetchUserIssuesData = null;
            })

            // Get Issue Tags Dropdown
            .addCase(getIssueTagsDropdown.pending, (state) => {
                state.getIssueTagsDropdownLoading = true;
                state.getIssueTagsDropdownError = null;
            })
            .addCase(getIssueTagsDropdown.fulfilled, (state, action) => {
                state.getIssueTagsDropdownLoading = false;
                state.getIssueTagsDropdownData = action.payload?.data || null;
                state.getIssueTagsDropdownError = null;
            })
            .addCase(getIssueTagsDropdown.rejected, (state, action) => {
                state.getIssueTagsDropdownLoading = false;
                state.getIssueTagsDropdownError = action.payload;
                state.getIssueTagsDropdownData = null;
            });
    },
});

export default issuesSlice.reducer;